import {createApi} from "@reduxjs/toolkit/query/react"
import {
    IBranch,
    IBranchMore,
    ILocalizedBranchText,
    IParamsAllBranchesList,
    IParamsBranchList,
    IBranchScheduleId,
    IResponseBranchScheduleList,
    IResponseBranchList
} from "./BranchTypes"
import {IBasicParams, IBasicResponse} from "../../types/api"
import baseQuery from "../../utils/apiIIKOConfig"
import {
    IBranchPaymentClickParams,
    IBranchPaymentMulticardParams,
    IBranchPaymentPaymeParams,
    IBranchAuditLogListParams,
    IBranchScheduleCreateParams,
    IBranchScheduleDeleteParams,
    IBranchScheduleListParams,
    IResponseBranchAuditLogList,
    IBranchScheduleUpdateParams,
    IBranchShippingParams,
    IBranchSyncResponse, IBranchUpdateDataParams,
    IBranchUpdateSettingsParams
} from "./BranchApiTypes"
import {
    getBranchEffectiveClosedTime,
    getBranchEffectiveOpenTime,
    getBranchEffectiveServiceModes,
    getBranchEffectiveStatus,
    toBranchDeliveryZoneApiValue
} from "./branchUtils"
import {getPaginationDecision} from "../../utils/pagination"
import {translateUi} from "../../i18n/translateUi"

const BRANCH_LIST_PAGE_SIZE = 100

const getLocalizedText = (value: ILocalizedBranchText) => {
    if (!value) return ""
    if (typeof value === "string") return value

    return value.ru || value.uz || Object.values(value).find(Boolean) || ""
}

const mapBranchListItemToBranch = (branch: IBranchMore): IBranch => ({
    id: branch.id,
    name: getLocalizedText(branch.name),
    address: getLocalizedText(branch.address),
    location: branch.location,
    status: getBranchEffectiveStatus(branch) || {
        id: "CLOSED",
        title: translateUi("Статус не указан")
    },
    picture_url: branch.picture_url,
    open_time: getBranchEffectiveOpenTime(branch),
    closed_time: getBranchEffectiveClosedTime(branch),
    phone_number: branch.phone_number || null,
    support_chat_url: branch.support_chat_url,
    delivery_duration_minutes: branch.delivery_duration_minutes,
    pickup_duration_minutes: branch.pickup_duration_minutes,
    service_mode: getBranchEffectiveServiceModes(branch)
})

export const branchApi = createApi({
    reducerPath: "branchApi",
    baseQuery,
    tagTypes: ["branches", "branchSchedules"],
    endpoints: build => ({
        getBranches: build.query<IBasicResponse<IBranch[]>, IBasicParams>({
            queryFn: async ({lang}, _queryApi, _extraOptions, fetchWithBQ) => {
                const branchesById = new Map<string, IBranch>()
                const seenSignatures = new Set<string>()
                let page = 1
                let responseMeta: Omit<IBasicResponse<IResponseBranchList>, "data"> = {
                    success: true
                }

                while (true) {
                    const response = await fetchWithBQ({
                        method: "POST",
                        url: "v1/branches/list",
                        headers: {
                            "Accept-Language": lang
                        },
                        body: {page, size: BRANCH_LIST_PAGE_SIZE}
                    })

                    if (response.error) {
                        return {error: response.error}
                    }

                    const responseData = response.data as IBasicResponse<IResponseBranchList>
                    if (!responseData?.success || !responseData.data) {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: responseData?.message || translateUi("Не удалось загрузить список филиалов")
                            }
                        }
                    }

                    const pageData = responseData.data
                    const content = pageData.content || []
                    const decision = getPaginationDecision({
                        requestedPage: page,
                        pageSize: BRANCH_LIST_PAGE_SIZE,
                        loadedCount: branchesById.size,
                        pageData,
                        seenSignatures,
                        getItemKey: (branch) => branch.id
                    })

                    responseMeta = {
                        success: responseData.success,
                        message: responseData.message
                    }
                    if (decision.type === "error") {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: translateUi("Не удалось загрузить полный список филиалов")
                            }
                        }
                    }

                    content.forEach((branch) => branchesById.set(branch.id, mapBranchListItemToBranch(branch)))
                    if (decision.type === "done") {
                        break
                    }

                    seenSignatures.add(decision.signature)
                    page = decision.nextPage
                }

                return {
                    data: {
                        ...responseMeta,
                        data: Array.from(branchesById.values())
                    }
                }
            },
            providesTags: ["branches"]
        }),
        getBranchesList: build.query<IBasicResponse<IResponseBranchList>, IParamsBranchList>({
            query: ({lang, page, size, time, weekday}) => {
                const body = {
                    page,
                    size,
                    ...(time ? {time} : {}),
                    ...(weekday ? {weekday} : {})
                }

                return {
                    method: "POST",
                    url: `v1/branches/list`,
                    headers: {
                        "Accept-Language": lang,
                    },
                    body
                }
            },
            providesTags: ["branches"]
        }),
        getAllBranchesList: build.query<IBasicResponse<IResponseBranchList>, IParamsAllBranchesList>({
            queryFn: async ({lang, time, weekday}, _queryApi, _extraOptions, fetchWithBQ) => {
                const branchesById = new Map<string, IBranchMore>()
                const seenSignatures = new Set<string>()
                let page = 1
                let responseMeta: Omit<IBasicResponse<IResponseBranchList>, "data"> = {success: true}

                while (true) {
                    const response = await fetchWithBQ({
                        method: "POST",
                        url: "v1/branches/list",
                        headers: {"Accept-Language": lang},
                        body: {
                            page,
                            size: BRANCH_LIST_PAGE_SIZE,
                            ...(time ? {time} : {}),
                            ...(weekday ? {weekday} : {})
                        }
                    })

                    if (response.error) {
                        return {error: response.error}
                    }

                    const responseData = response.data as IBasicResponse<IResponseBranchList>
                    if (!responseData?.success || !responseData.data) {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: responseData?.message || translateUi("Не удалось загрузить список филиалов")
                            }
                        }
                    }

                    const pageData = responseData.data
                    const content = pageData.content || []
                    const decision = getPaginationDecision({
                        requestedPage: page,
                        pageSize: BRANCH_LIST_PAGE_SIZE,
                        loadedCount: branchesById.size,
                        pageData,
                        seenSignatures,
                        getItemKey: (branch) => branch.id
                    })

                    if (decision.type === "error") {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: translateUi("Не удалось загрузить полный список филиалов")
                            }
                        }
                    }

                    content.forEach((branch) => branchesById.set(branch.id, branch))
                    responseMeta = {
                        success: responseData.success,
                        message: responseData.message
                    }

                    if (decision.type === "done") {
                        break
                    }

                    seenSignatures.add(decision.signature)
                    page = decision.nextPage
                }

                const content = Array.from(branchesById.values())
                return {
                    data: {
                        ...responseMeta,
                        data: {
                            content,
                            page: 1,
                            size: content.length,
                            total_elements: content.length
                        }
                    }
                }
            },
            providesTags: ["branches"]
        }),
        getBranchAuditLogs: build.query<IBasicResponse<IResponseBranchAuditLogList>, IBranchAuditLogListParams>({
            query: ({lang, page, size, branch_id, date_from, date_to}) => ({
                method: "POST",
                url: "v1/branches/audit-logs/list",
                headers: {
                    "Accept-Language": lang,
                },
                body: {
                    page,
                    size,
                    ...(branch_id ? {branch_id} : {}),
                    ...(date_from ? {date_from} : {}),
                    ...(date_to ? {date_to} : {})
                }
            }),
            providesTags: ["branches"]
        }),
        syncBranches: build.mutation<IBasicResponse<IBranchSyncResponse>, IBasicParams>({
            query: ({lang}) => ({
                method: "POST",
                url: `v1/branches/sync`,
                headers: {
                    "Accept-Language": lang
                }
            }),
            invalidatesTags: ["branches"]
        }),
        updateDataById: build.mutation<IBasicResponse<void>, IBranchUpdateDataParams>({
            query: ({lang, id, name, address, picture_url}) => ({
                method: "PATCH",
                url: `v1/branches/${id}`,
                headers: {
                    "Accept-Language": lang,
                },
                body: {
                    name,
                    address,
                    picture_url
                }
            }),
            invalidatesTags: ["branches"]
        }),
        getBranchSchedules: build.query<IBasicResponse<IResponseBranchScheduleList>, IBranchScheduleListParams>({
            query: ({lang, page, size, branch_id}) => ({
                method: "POST",
                url: `v1/branches/schedules/list`,
                headers: {
                    "Accept-Language": lang,
                },
                body: {
                    page,
                    size,
                    ...(branch_id ? {branch_id} : {})
                }
            }),
            providesTags: ["branchSchedules"]
        }),
        createBranchSchedule: build.mutation<IBasicResponse<{id: IBranchScheduleId}>, IBranchScheduleCreateParams>({
            query: ({lang, branch_id, branch_status, opening_time, closing_time, weekday_list, service_mode_list, delivery_zone}) => ({
                method: "POST",
                url: `v1/branches/schedules`,
                headers: {
                    "Accept-Language": lang,
                },
                body: {
                    branch_id,
                    branch_status,
                    opening_time,
                    closing_time,
                    weekday_list,
                    service_mode_list,
                    delivery_zone: toBranchDeliveryZoneApiValue(delivery_zone)
                }
            }),
            invalidatesTags: ["branches", "branchSchedules"]
        }),
        updateBranchSchedule: build.mutation<IBasicResponse<void>, IBranchScheduleUpdateParams>({
            query: ({lang, id, branch_status, opening_time, closing_time, weekday_list, service_mode_list, delivery_zone}) => ({
                method: "PUT",
                url: `v1/branches/schedules/${id}`,
                headers: {
                    "Accept-Language": lang,
                },
                body: {
                    branch_status,
                    opening_time,
                    closing_time,
                    weekday_list,
                    service_mode_list,
                    delivery_zone: toBranchDeliveryZoneApiValue(delivery_zone)
                }
            }),
            invalidatesTags: ["branches", "branchSchedules"]
        }),
        deleteBranchSchedule: build.mutation<IBasicResponse<void>, IBranchScheduleDeleteParams>({
            query: ({lang, id}) => ({
                method: "DELETE",
                url: `v1/branches/schedules/${id}`,
                headers: {
                    "Accept-Language": lang
                }
            }),
            invalidatesTags: ["branches", "branchSchedules"]
        }),
        updateSettingsById: build.mutation<IBasicResponse<void>, IBranchUpdateSettingsParams>({
            query: ({
                lang,
                id,
                phone_number,
                support_chat_url,
                delivery_duration_minutes,
                pickup_duration_minutes,
                location
            }) => ({
                method: "PATCH",
                url: `/v1/branches/${id}/settings`,
                headers: {
                    "Accept-Language": lang,
                },
                body: {
                    phone_number,
                    support_chat_url,
                    delivery_duration_minutes,
                    pickup_duration_minutes,
                    ...(location ? {location} : {})
                }
            }),
            invalidatesTags: ["branches"]
        }),
        updateShippingById: build.mutation<IBasicResponse<void>, IBranchShippingParams>({
            query: ({
                lang,
                id,
                shipping_id,
                telegram_group_chat_id,
                max_active_orders_per_courier,
                courier_acceptance_radius_meters,
                courier_completion_radius_meters
            }) => ({
                method: "PATCH",
                url: `/v1/branches/${id}/shipping`,
                headers: {
                    "Accept-Language": lang,
                },
                body: {
                    shipping_id,
                    telegram_group_chat_id,
                    max_active_orders_per_courier,
                    courier_acceptance_radius_meters,
                    courier_completion_radius_meters
                }
            }),
            invalidatesTags: ["branches"]
        }),
        updatePaymentPaymeById: build.mutation<IBasicResponse<void>, IBranchPaymentPaymeParams>({
            query: ({lang, id, payme_client_id, payme_client_key}) => ({
                method: "PATCH",
                url: `/v1/branches/${id}/payment/payme`,
                headers: {
                    "Accept-Language": lang,
                },
                body: {
                    payme_client_id,
                    payme_client_key
                }
            }),
            invalidatesTags: ["branches"]
        }),
        updatePaymentClickById: build.mutation<IBasicResponse<void>, IBranchPaymentClickParams>({
            query: ({lang, id, ...body}) => ({
                method: "PATCH",
                url: `/v1/branches/${id}/payment/click`,
                headers: {
                    "Accept-Language": lang,
                },
                body
            }),
            invalidatesTags: ["branches"]
        }),
        updatePaymentMulticardById: build.mutation<IBasicResponse<void>, IBranchPaymentMulticardParams>({
            query: ({lang, id, store_id}) => ({
                method: "PATCH",
                url: `/v1/branches/${id}/payment/multicard`,
                headers: {
                    "Accept-Language": lang,
                },
                body: {
                    store_id
                }
            }),
            invalidatesTags: ["branches"]
        })
    })
})

export const {
    useCreateBranchScheduleMutation,
    useDeleteBranchScheduleMutation,
    useGetBranchAuditLogsQuery,
    useGetBranchSchedulesQuery,
    useGetAllBranchesListQuery,
    useGetBranchesListQuery,
    useGetBranchesQuery,
    useSyncBranchesMutation,
    useUpdateBranchScheduleMutation,
    useUpdateDataByIdMutation,
    useUpdateSettingsByIdMutation,
    useUpdateShippingByIdMutation,
    useUpdatePaymentClickByIdMutation,
    useUpdatePaymentPaymeByIdMutation,
    useUpdatePaymentMulticardByIdMutation
} = branchApi
